// Angular
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
// NGRX
import { Store } from '@ngrx/store';
// Translate
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
// RxJS
import { finalize, takeUntil, tap } from 'rxjs/operators';
// Auth
import { AuthNoticeService, AuthService, Register, User } from '../../../../core/auth/';
import {
	isControlHasError,
	getEmailErrorMessage,
	getPasswordErrorMessage,
	getConfirmPasswordErrorMessage,
	getNameErrorMessage
} from '../../../../core/auth/_utils/auth-validation.util';
import { AppState } from '../../../../core/reducers';
import { ConfirmPasswordValidator } from '../register/confirm-password.validator';
import { environment } from '../../../../../environments/environment';
import CryptoES from 'crypto-es';

@Component({
	selector: 'kt-register-admin',
	templateUrl: './register-admin.component.html',
	styleUrls: []
})

export class RegisterAdminComponent implements OnInit, OnDestroy {
	registerForm: UntypedFormGroup;
	loading = false;
	errors: any = [];
	isLogoAvailable: any;
	nameErrorMessage: string = '';
	emailErrorMessage: string = '';
	passwordErrorMessage: string = '';
	confirmPasswordErrorMessage: string = '';
	imagePaths = {
		uptimeLogoOriginal: "assets/media/logos/logo-loader-colour.svg"
	}

	private unsubscribe: Subject<any>; // Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/

	/**
	 * Component constructor
	 *
	 * @param authNoticeService: AuthNoticeService
	 * @param translate: TranslateService
	 * @param router: Router
	 * @param auth: AuthService
	 * @param store: Store<AppState>
	 * @param fb: FormBuilder
	 * @param cdr
	 */
	constructor(
		private readonly authNoticeService: AuthNoticeService,
		private readonly translate: TranslateService,
		private readonly router: Router,
		private readonly auth: AuthService,
		private readonly store: Store<AppState>,
		private readonly fb: UntypedFormBuilder,
		private readonly cdr: ChangeDetectorRef
	) {
		this.unsubscribe = new Subject();
	}

	/*
	 * @ Lifecycle sequences => https://angular.io/guide/lifecycle-hooks
	*/

	/**
	 * On init
	 */
	ngOnInit() {
		this.isLogoAvailable = JSON.parse(localStorage.getItem('layoutConfig'))['logo'];
		this.initRegisterForm();

		this.registerForm.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe(() => {
			this.updateErrors();
		});
		this.updateErrors();
	}

	/*
	* On destroy
	*/
	ngOnDestroy(): void {
		this.unsubscribe.next(undefined);
		this.unsubscribe.complete();
		this.loading = false;
	}

	/**
	 * Form initalization
	 * Default params, validators
	 */
	initRegisterForm() {
		this.registerForm = this.fb.group({
			name: ['', Validators.compose([
				Validators.required,
				Validators.minLength(3),
				Validators.maxLength(100)
			])
			],
			email: ['', Validators.compose([
				Validators.required,
				Validators.email,
				Validators.minLength(3),
				// https://stackoverflow.com/questions/386294/what-is-the-maximum-length-of-a-valid-email-address
				Validators.maxLength(320)
			]),
			],
			// username: ['', Validators.compose([
			// 	Validators.required,
			// 	Validators.minLength(3),
			// 	Validators.maxLength(100)
			// ]),
			// ],
			password: ['', Validators.compose([
				Validators.required,
				Validators.maxLength(100),
				Validators.pattern('(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^a-zA-Z0-9]).{8,}')
			])],
			confirmPassword: ['', Validators.compose([
				Validators.required,
				Validators.maxLength(100),
				Validators.pattern('(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^a-zA-Z0-9]).{8,}')
			])],
			agree: [false, Validators.compose([Validators.required])]
		}, {
			validator: ConfirmPasswordValidator.MatchPassword
		});
	}

	/**
	 * Form Submit
	 */
	submit() {
		const controls = this.registerForm.controls;

		// check form
		if (this.registerForm.invalid) {
			Object.keys(controls).forEach(controlName =>
				controls[controlName].markAsTouched()
			);
			this.updateErrors();
			return;
		}

		this.loading = true;

		if (!controls['agree'].value) {
			// you must agree the terms and condition
			// checkbox cannot work inside mat-form-field https://github.com/angular/material2/issues/7891
			this.authNoticeService.setNotice('You must agree the terms and condition', 'danger');
			return;
		}

		const _user: User = new User();
		_user.clear();
		_user.email = controls['email'].value;
		//_user.username = controls['username'].value;
		_user.name = controls['name'].value;
		//_user.organization = controls['organization'].value;
		const encryptedPassword = CryptoES.AES.encrypt(controls['password'].value, environment.runtime.authTokenKey).toString();
		_user.password = encryptedPassword;
		//_user.roles = [];
		this.auth.register(_user).pipe(
			tap(user => {
				console.log(user)
				if (user.status == 200) {
					alert(this.translate.instant('AUTH.REGISTER.EMAIL_ALREADY_REGISTERED'))
					this.authNoticeService.setNotice(this.translate.instant('AUTH.VALIDATION.INVALID_LOGIN'), 'danger');
				} else if (user.status == 201) {
					this.store.dispatch(new Register({ authToken: user.headers.get('x-auth-token') }));
					// pass notice message to the login page
					this.authNoticeService.setNotice(this.translate.instant('AUTH.REGISTER.SUCCESS'), 'success');
					//this.router.navigateByUrl('/auth/login');
					this.router.navigateByUrl('/web/dashboard');
				} else {
					this.authNoticeService.setNotice(this.translate.instant('AUTH.VALIDATION.INVALID_LOGIN'), 'danger');
				}
			}),
			takeUntil(this.unsubscribe),
			finalize(() => {
				this.loading = false;
				this.cdr.detectChanges();
			})
		).subscribe();
	}

	isControlHasError(controlName: string, validationType: string): boolean {
		return isControlHasError(this.registerForm.get(controlName), validationType);
	}

	updateErrors(): void {
		this.nameErrorMessage = getNameErrorMessage(this.registerForm.get('name'), this.translate);
		this.emailErrorMessage = getEmailErrorMessage(this.registerForm.get('email'), this.translate, {
			fieldName: this.translate.instant('AUTH.INPUT.EMAIL'),
			isInvalidField: true
		});
		this.passwordErrorMessage = getPasswordErrorMessage(this.registerForm.get('password'), this.translate);
		this.confirmPasswordErrorMessage = getConfirmPasswordErrorMessage(this.registerForm.get('confirmPassword'), this.translate);
	}
}
